import { useEffect, useState, useRef } from "react";
import { apiQueryToDownloadData } from "../../common/services/apiBaseUrlConfig";
import { MotifToast, MotifProgressLoader } from "@ey-xd/motif-react";
import "./FAQViewer.scss";
import { decryptSasUri } from "../../common/utility/cryptoUtils";
import logger from "../../common/utils/logger";

export default function FAQViewer() {
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchFAQFile = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;

      try {
        setIsLoading(true);
        const response = await apiQueryToDownloadData.get(
          `${process.env.REACT_APP_API_URL_US}/api/support/faqs/view`
        );

        if (response && response.data) {
          const encryptedSasUri = response?.data?.encryptedSasUri;

          if (encryptedSasUri) {
            const decryptedSasUri = decryptSasUri(encryptedSasUri);
            if (!decryptedSasUri) {
              setIsToastVisible(true);
              setToastTitle("Load failed");
              setToastMessage("Unable to decrypt the file URL.");
              return;
            }

            // Fetch the file from the decrypted URI
            const fileResponse = await fetch(decryptedSasUri);
            const blob = await fileResponse.blob();

            // Create a blob URL
            const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            setPdfUrl(url);
          } else {
            setIsToastVisible(true);
            setToastTitle("Load failed");
            setToastMessage("The file you are trying to view does not exist.");
          }
        } else {
          setIsToastVisible(true);
          setToastTitle("Load failed");
          setToastMessage("Something went wrong while loading the file.");
        }
      } catch (error) {
        logger.error("Error loading FAQ:", error);
        setIsToastVisible(true);
        setToastTitle("Load failed");
        setToastMessage("Something went wrong while loading the file.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFAQFile();

    // Cleanup function to revoke object URL when component unmounts
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    if (isToastVisible) {
      setTimeout(() => {
        setIsToastVisible(false);
        setToastTitle("");
        setToastMessage("");
      }, 5000);
    }
  }, [isToastVisible]);

  return (
    <>
      <div className="faq-viewer-container">
        {isLoading ? (
          <div className="loading-container">
            <MotifProgressLoader show variant="default" />            
          </div>
        ) : pdfUrl ? (
          <div className="pdf-container">
            <iframe
              src={pdfUrl}
              title="FAQs Document"
              className="pdf-iframe"
              type="application/pdf"
            />
          </div>
        ) : (
          <div className="error-container">
            <p>Failed to load the FAQ document. Please try again later.</p>
          </div>
        )}
      </div>

      {isToastVisible && (
        <MotifToast
          variant="error"
          className="faq-viewer-toast"
          onClick={() => setIsToastVisible(false)}
          position="top"
          style={{ textAlign: 'left' }}
        >
          <div style={{ textAlign: 'left' }}>
            <strong>{toastTitle}</strong><br />
            {toastMessage}
          </div>
        </MotifToast>
      )}
    </>
  );
}